// STAGING-LAUNCH-READINESS-002-R2 / COMMERCE-176B-R1 — customer-facing status copy.
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { CustomerReceiptState, CustomerDeliveryState } from "./order-history-v2.js";
import {
  canonicalOrderTitle,
  codPaymentCustomerLabel,
  isCodPaymentMethod,
  mapToCanonicalCustomerStage,
} from "./canonical-order-projection-v2.js";

/** STAGING-LAUNCH-READINESS-002-R2-R2 — client-side mirror of the worker's paid-status
 * set (customerCancelBlockReason / paymentStatusLabel). Gates cancel/payment-recovery UI. */
export const CUSTOMER_PAID_PAYMENT_STATUSES: readonly string[] = [
  "paid",
  "payment_paid",
  "payment_confirmed_manual",
  "payment_confirmed_bank_iban",
  "paid_bank_iban",
  "paid_online_novapay",
];

export function isOrderPaidStatus(paymentStatus: string | null | undefined): boolean {
  const ps = String(paymentStatus ?? "").toLowerCase();
  if (CUSTOMER_PAID_PAYMENT_STATUSES.includes(ps)) return true;
  // Ledger may use paid_* projections (e.g. paid_online_novapay).
  return ps === "paid" || ps.startsWith("paid_");
}

/** Operational sub-statuses that mean fulfillment already started (mirrors ops-bridge.ts). */
export const CUSTOMER_FULFILLMENT_BLOCKED_STATUSES: readonly string[] = [
  "np_details_received",
  "ready_to_ship",
  "shipped_manual_no_live_ttn",
];

export function paymentStatusLabel(
  lang: StoreV2Lang,
  paymentStatus: string,
  paymentMethod?: string | null,
  opts?: { ttn_status?: string | null },
): string {
  const en = lang === "en";
  if (isCodPaymentMethod(paymentMethod)) {
    const settled =
      isOrderPaidStatus(paymentStatus) || /cod_collected|cod_paid|settled/i.test(paymentStatus);
    const hasTtn = Boolean(
      opts?.ttn_status && !/^(not_live|pending|no_ttn|none|)$/i.test(String(opts.ttn_status)),
    );
    // COD customer copy (Післяплата / Cash on delivery) lives in codPaymentCustomerLabel.
    return codPaymentCustomerLabel(lang, { hasTtn, settled });
  }
  if (isOrderPaidStatus(paymentStatus)) {
    const novapay =
      /novapay/i.test(String(paymentMethod ?? "")) || /novapay/i.test(String(paymentStatus ?? ""));
    if (novapay) {
      return en ? "✓ Paid online" : "✓ Оплачено онлайн";
    }
    if (/bank_transfer|iban|fop/i.test(String(paymentMethod ?? "")) || paymentStatus === "payment_confirmed_bank_iban") {
      return en ? "✅ Payment confirmed by the bank" : "✅ Оплату підтверджено банком";
    }
    return en ? "Paid" : "Оплачено";
  }
  const ps = String(paymentStatus ?? "").toLowerCase();
  if (ps === "authorized_hold" || ps === "holded" || ps === "hold_confirmed") {
    return en
      ? "✓ Payment confirmed. Funds reserved until the shop confirms the order"
      : "✓ Оплату підтверджено. Кошти зарезервовано до підтвердження замовлення";
  }
  if (ps === "payment_not_required") {
    return en ? "No online payment required" : "Онлайн-оплата не потрібна";
  }
  switch (ps) {
    case "payment_in_review":
      return en ? "Payment under review" : "Оплата на перевірці";
    case "payment_failed":
    case "payment_failed_novapay":
      return en ? "Payment not completed" : "Оплата не завершена";
    case "payment_expired":
    case "payment_expired_novapay":
      return en ? "Payment not completed" : "Оплата не завершена";
    case "payment_pending":
    default:
      if (/bank_transfer|iban|fop/i.test(String(paymentMethod ?? ""))) {
        return en ? "Waiting for bank confirmation" : "Очікуємо підтвердження банку";
      }
      return en ? "Awaiting payment" : "Очікуємо оплату";
  }
}

export function packingStatusLabel(lang: StoreV2Lang, topStatus: string | null): string {
  const stage = mapToCanonicalCustomerStage({ order_top_status: topStatus });
  return canonicalOrderTitle(lang, stage);
}

/** Prefer order_top_status over legacy status=placed after accept. */
export function packingStatusFromOrder(
  lang: StoreV2Lang,
  order: {
    order_top_status?: string | null | undefined;
    status?: string | null | undefined;
    primary_order_state?: string | null | undefined;
    ttn_status?: string | null | undefined;
    delivery_status?: string | null | undefined;
    canonical_projection?: { primary_order_state?: string | null | undefined } | null | undefined;
  },
): string {
  const stage = mapToCanonicalCustomerStage({
    order_top_status: order.order_top_status ?? null,
    status: order.status ?? null,
    primary_order_state:
      order.canonical_projection?.primary_order_state ?? order.primary_order_state ?? null,
    ttn_status: order.ttn_status ?? null,
    delivery_status: order.delivery_status ?? null,
  });
  return canonicalOrderTitle(lang, stage);
}

export function ttnStatusLabel(lang: StoreV2Lang, ttnStatus: string): string {
  const en = lang === "en";
  if (ttnStatus === "created_live" || ttnStatus === "created_later" || ttnStatus === "ttn_created") {
    return en ? "TTN created" : "ТТН створено";
  }
  if (ttnStatus === "handed_to_carrier" || ttnStatus === "in_transit") {
    return en ? "Handed to Nova Poshta" : "Передано Новій пошті";
  }
  if (ttnStatus === "arrived_warehouse" || ttnStatus === "ready_for_pickup") {
    return en ? "Parcel at the branch" : "Посилка у відділенні";
  }
  if (ttnStatus === "pending_manual") {
    return en ? "Shipment pending" : "Очікує відправлення";
  }
  return en
    ? "Not shipped yet — tracking number will appear here"
    : "Ще не відправлено — номер ТТН зʼявиться тут";
}

/** STAGING-LAUNCH-READINESS-002-R2-R2 — «🚚 Доставка і ТТН» state label (read-only). */
export function deliveryStateLabel(lang: StoreV2Lang, state: CustomerDeliveryState): string {
  const en = lang === "en";
  switch (state) {
    case "ttn_created":
      return en ? "Shipped — tracking number below" : "Відправлено — номер ТТН нижче";
    case "pending_ttn":
      return en
        ? "Address confirmed — tracking number pending"
        : "Адресу підтверджено — номер ТТН ще не присвоєно";
    case "not_set":
    default:
      return en ? "Delivery details not set yet" : "Дані доставки ще не вказано";
  }
}

export function receiptStateLabel(lang: StoreV2Lang, state: CustomerReceiptState): string {
  const en = lang === "en";
  switch (state) {
    case "not_needed":
      return en ? "Documents have not been generated yet" : "Документи ще не сформовано";
    case "unpaid":
    case "awaiting_payment":
      return en ? "Waiting for payment" : "Очікуємо оплату";
    case "paid":
      return en ? "Payment received" : "Оплату отримано";
    case "queued":
    case "creating":
    case "awaiting_fiscalization":
    case "forming":
      return en ? "Preparing electronic receipt" : "Формуємо електронний чек";
    case "ready":
    case "fiscalized":
      return en ? "✓ Fiscalized" : "✓ Фіскалізовано";
    case "delay":
      return en ? "Temporary delay" : "Тимчасова затримка";
    case "error":
      return en ? "Receipt creation error" : "Помилка формування чека";
    case "identity_missing":
      return en
        ? "Unavailable — missing customer identity"
        : "Недоступний через відсутність customer identity";
    case "refund_pending":
      return en ? "Refund in progress" : "Повернення оформлюється";
    case "refund_done":
      return en ? "Refund completed" : "Повернення завершено";
    case "unavailable":
    default:
      return en
        ? "Receipt is temporarily unavailable — contact the manager"
        : "Чек тимчасово недоступний — напишіть менеджеру";
  }
}
